import React, { useState, useEffect, useRef } from 'react';

/**
 * COMPONENTE: ANECDOTES FULLSCREEN (PORTADA 1920x1080 A 2 COLUMNAS)
 * ------------------------------------------------------------------
 * Diseñado para la pantalla gigante del estadio y overlays completos de transmisión.
 * Especificaciones de la HU:
 * 1. Portada 1920x1080 a 2 columnas.
 * 2. Columna Izquierda: Logotipo oficial superior, contenedor estilizado con la anécdota y autor.
 *    Cuando cambia de historia, solo el texto y autor se desvanecen suavemente (fadeOut de 300ms)
 *    y entra el nuevo texto con efecto máquina de escribir (35ms/letra).
 * 3. Columna Derecha: Título "ESCANEA EL CÓDIGO Y CUÉNTANOS TU MEJOR ANÉCDOTA",
 *    imagen QR fija e ininterrumpida `/img/page/qr/anecdote-qr-fondo-gris-con-parametros.png`,
 *    y pie de créditos "Hecho con ♥ por Interaktia | 2026".
 * 4. Limpieza de pantalla (action: "hide" o "clear"): Transición de salida suave (300ms).
 * ------------------------------------------------------------------
 */
export const AnecdotesFullScreen = ({ data }) => {
    // Estados para la transición del texto en la columna izquierda
    // 'idle' | 'typing' | 'visible' | 'fadingOut'
    const [textState, setTextState] = useState('idle');
    const [messageData, setMessageData] = useState(null);
    const [pendingData, setPendingData] = useState(null);
    const [displayedText, setDisplayedText] = useState('');

    const textStateRef = useRef(textState);
    useEffect(() => {
        textStateRef.current = textState;
    }, [textState]);

    // 1. Recepción y encolamiento de anécdotas
    useEffect(() => {
        if (!data) return;

        // Comando de Ocultar / Limpiar Pantalla
        if (data.action === 'hide' || data.action === 'clear') {
            setPendingData(null);
            if (textStateRef.current !== 'idle' && textStateRef.current !== 'fadingOut') {
                setTextState('fadingOut');
            }
            return;
        }

        // Si no es comando hide y tiene texto o es show
        const rawText = data.message || data.content || data.text;
        if (!rawText) return;

        const normalized = {
            id: data.id,
            text: rawText,
            sender: data.sender || 'Anónimo'
        };

        if (textStateRef.current === 'idle' || !messageData) {
            setMessageData(normalized);
            setTextState('typing');
        } else {
            // Ya hay una historia en pantalla: iniciamos fadeOut suave de 300ms y encolamos
            setPendingData(normalized);
            if (textStateRef.current !== 'fadingOut') {
                setTextState('fadingOut');
            }
        }
    }, [data]);

    // 2. Control de transición suave (fadeOut de 300ms)
    useEffect(() => {
        let timeout;
        if (textState === 'fadingOut') {
            timeout = setTimeout(() => {
                if (pendingData) {
                    setMessageData(pendingData);
                    setPendingData(null);
                    setDisplayedText('');
                    setTextState('typing');
                } else {
                    setMessageData(null);
                    setDisplayedText('');
                    setTextState('idle');
                }
            }, 300); // fadeOut suave de 300ms según especificación
        }

        return () => clearTimeout(timeout);
    }, [textState, pendingData]);

    // 3. Efecto Máquina de Escribir (35ms por letra)
    useEffect(() => {
        if (textState === 'typing' && messageData) {
            let currentIndex = 0;
            const fullText = messageData.text;
            setDisplayedText('');

            const interval = setInterval(() => {
                setDisplayedText(fullText.substring(0, currentIndex + 1));
                currentIndex++;

                if (currentIndex >= fullText.length) {
                    clearInterval(interval);
                    setTextState('visible');
                }
            }, 35); // 35ms por letra según especificación

            return () => clearInterval(interval);
        }
    }, [textState, messageData]);

    return (
        <div className="anecdotes-fullscreen" data-testid="anecdotes-fullscreen-container">
            {/* COLUMNA IZQUIERDA: Logotipo superior, contenedor estilizado con anécdota y autor */}
            <div className="anecdotes-fullscreen__left">
                {/* Cabecera oficial con Logotipo ADSL */}
                <div className="anecdotes-fullscreen__header">
                    <img
                        src="/img/brand/logotipo-adsl-blanco.png"
                        alt="Club Atlético de San Luis"
                        className="anecdotes-fullscreen__logo"
                    />
                    <div className="anecdotes-fullscreen__badge-container">
                        <span className="anecdotes-fullscreen__badge">
                            <i className="bi bi-chat-quote-fill me-2"></i> TU MEJOR ANÉCDOTA
                        </span>
                    </div>
                </div>

                {/* Contenedor Estilizado de la Historia */}
                <div className="anecdotes-fullscreen__card-wrapper">
                    <div className={`anecdotes-fullscreen__card anecdotes-fullscreen__card--${textState}`}>
                        <div className="anecdotes-fullscreen__quote-icon anecdotes-fullscreen__quote-icon--start">
                            &ldquo;
                        </div>

                        <div className="anecdotes-fullscreen__story-body">
                            {messageData ? (
                                <>
                                    <p className="anecdotes-fullscreen__story-text">
                                        {displayedText}
                                        {textState === 'typing' && (
                                            <span className="anecdotes-fullscreen__cursor">|</span>
                                        )}
                                    </p>
                                    <div className="anecdotes-fullscreen__author">
                                        <i className="bi bi-person-circle me-2"></i>
                                        <span className="anecdotes-fullscreen__author-label">Enviado por:</span>
                                        <strong className="anecdotes-fullscreen__author-name ms-1">
                                            {messageData.sender}
                                        </strong>
                                    </div>
                                </>
                            ) : (
                                <div className="anecdotes-fullscreen__placeholder">
                                    <i className="bi bi-megaphone-fill mb-3 d-block text-warning" style={{ fontSize: '3rem' }}></i>
                                    <p className="anecdotes-fullscreen__placeholder-title">
                                        ¡Cuéntanos tu mejor momento con el Atleti!
                                    </p>
                                    <p className="anecdotes-fullscreen__placeholder-subtitle">
                                        Escanea el código QR de la derecha y comparte tu historia. Podrá ser proyectada aquí en el estadio.
                                    </p>
                                </div>
                            )}
                        </div>

                        <div className="anecdotes-fullscreen__quote-icon anecdotes-fullscreen__quote-icon--end">
                            &rdquo;
                        </div>
                    </div>
                </div>
            </div>

            {/* COLUMNA DERECHA: Título QR, QR Fijo e Ininterrumpido, y Pie de Créditos Interaktia */}
            <div className="anecdotes-fullscreen__right">
                <div className="anecdotes-fullscreen__qr-panel">
                    {/* Título de la dinámica */}
                    <h2 className="anecdotes-fullscreen__qr-title">
                        ESCANEA EL CÓDIGO Y CUÉNTANOS TU MEJOR ANÉCDOTA
                    </h2>

                    {/* Imagen QR Fija e Ininterrumpida */}
                    <div className="anecdotes-fullscreen__qr-wrapper">
                        <img
                            src="/img/page/qr/anecdote-qr-fondo-gris-con-parametros.png"
                            alt="Código QR Tu Mejor Anécdota"
                            className="anecdotes-fullscreen__qr-img"
                        />
                    </div>

                    {/* Instrucciones breves */}
                    <div className="anecdotes-fullscreen__instructions">
                        <div className="anecdotes-fullscreen__instruction-step">
                            <span className="anecdotes-fullscreen__step-number">1</span>
                            <span>Abre tu cámara</span>
                        </div>
                        <div className="anecdotes-fullscreen__instruction-step">
                            <span className="anecdotes-fullscreen__step-number">2</span>
                            <span>Escanea el QR</span>
                        </div>
                        <div className="anecdotes-fullscreen__instruction-step">
                            <span className="anecdotes-fullscreen__step-number">3</span>
                            <span>Escribe tu historia</span>
                        </div>
                    </div>

                    {/* Pie de créditos institucional Interaktia */}
                    <div className="anecdotes-fullscreen__footer">
                        <p className="anecdotes-fullscreen__credits">
                            Hecho con <span className="text-danger">♥</span> por Interaktia | 2026
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AnecdotesFullScreen;
